import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { logout } from "@/store/feature/auth/authSlice";
import { useRouter } from '@/compat/navigation';
import SessionExpiryTimer from "@/lib/sessionExpiryTimer";
import { getTokenExpiryTime } from "@/lib/tokenRefresh";

const ACTIVITY_SYNC_KEY = "lastActivity";
const SESSION_LOGOUT_KEY = "session_logout";

export function useIdleTimer(
  timeoutInMinutes: number | null,
  isAuthenticated: boolean = true,
  modalRef?: React.MutableRefObject<{
    showIdleWarning: (onStayLoggedIn: () => void, onLogout: () => void) => void;
    closeModal: () => void;
  }>
) {
  const dispatch = useDispatch();
  const router = useRouter();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleLogout = useCallback(() => {
    dispatch(logout());
    if (modalRef?.current?.closeModal) {
      modalRef.current.closeModal();
    }

    // Broadcast logout to other tabs
    if (typeof window !== "undefined") {
      localStorage.setItem(SESSION_LOGOUT_KEY, Date.now().toString());
      // Use window.location for more reliable navigation
      window.location.href = "/login";
    } else {
      router.push("/login");
    }
  }, [dispatch, router, modalRef]);

  // Define handleIdleWarning first (used by resetTimer)
  const handleIdleWarning = useCallback(() => {
    // Check if user became active before showing modal
    const lastActivity = localStorage.getItem(ACTIVITY_SYNC_KEY);
    const now = Date.now();
    const oneMinuteAgo = now - 60 * 1000;

    if (lastActivity && parseInt(lastActivity) > oneMinuteAgo) {
      // User is active - reset timer instead of showing warning
      if (timeoutInMinutes && timeoutInMinutes > 0) {
        // Use timeoutInMinutes directly to avoid circular dependency
        const resetFn = () => {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
          if (modalRef?.current?.closeModal) modalRef.current.closeModal();

          if (typeof window !== "undefined") {
            localStorage.setItem(ACTIVITY_SYNC_KEY, Date.now().toString());
          }

          const warningTime = (timeoutInMinutes - 1) * 60 * 1000;
          if (warningTime > 0) {
            warningTimeoutRef.current = setTimeout(() => handleIdleWarning(), warningTime);
          }

          const logoutTime = timeoutInMinutes * 60 * 1000;
          timeoutRef.current = setTimeout(() => {
            SessionExpiryTimer.setIdleLogoutInProgress(true);
            handleLogout();
          }, logoutTime);
        };
        resetFn();
      }
      return;
    }

    if (modalRef?.current?.showIdleWarning) {
      // Mark idle logout in progress to prevent token expiry modal
      SessionExpiryTimer.setIdleLogoutInProgress(true);

      const resetFn = () => {
        SessionExpiryTimer.setIdleLogoutInProgress(false);
        if (modalRef?.current?.closeModal) modalRef.current.closeModal();

        if (!timeoutInMinutes || timeoutInMinutes <= 0) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);

        if (typeof window !== "undefined") {
          localStorage.setItem(ACTIVITY_SYNC_KEY, Date.now().toString());
        }

        const warningTime = (timeoutInMinutes - 1) * 60 * 1000;
        if (warningTime > 0) {
          warningTimeoutRef.current = setTimeout(() => handleIdleWarning(), warningTime);
        }

        const logoutTime = timeoutInMinutes * 60 * 1000;
        timeoutRef.current = setTimeout(() => {
          SessionExpiryTimer.setIdleLogoutInProgress(true);
          handleLogout();
        }, logoutTime);
      };

      modalRef.current.showIdleWarning(
        resetFn,
        handleLogout // User clicks "Log Out Now"
      );

      // Monitor activity while warning is shown - auto-reset if user becomes active
      const activityCheck = setInterval(() => {
        const lastActivity = localStorage.getItem(ACTIVITY_SYNC_KEY);
        const now = Date.now();
        const oneMinuteAgo = now - 60 * 1000;

        if (lastActivity && parseInt(lastActivity) > oneMinuteAgo) {
          // User became active - reset timer and close modal
          clearInterval(activityCheck);
          SessionExpiryTimer.setIdleLogoutInProgress(false);
          if (modalRef?.current?.closeModal) {
            modalRef.current.closeModal();
          }
          resetFn();
        }
      }, 2000); // Check every 2 seconds

      // Clear interval when modal closes or logout happens
      if (timeoutInMinutes) {
        setTimeout(() => clearInterval(activityCheck), timeoutInMinutes * 60 * 1000);
      }
    } else {
      console.error("❌ Modal ref not available");
    }
  }, [modalRef, timeoutInMinutes, handleLogout]);

  // Define resetTimer (uses handleIdleWarning)
  const resetTimer = useCallback(() => {
    // Early return if no timeout is set
    if (!timeoutInMinutes || timeoutInMinutes <= 0) {
      return;
    }

    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }

    // Close warning modal if open (user became active)
    if (modalRef?.current?.closeModal) {
      const token = localStorage.getItem("access_token");
      const expiryInMs = getTokenExpiryTime(token) || 0;
      const now = Date.now();
      const remainingMs = expiryInMs - now;

      if (remainingMs <= 30_000) {
        return null; // No need to close if token is expiring soon
      }
      modalRef.current.closeModal();
    }

    // Update last activity timestamp in localStorage (syncs across tabs)
    if (typeof window !== "undefined") {
      const now = Date.now().toString();
      localStorage.setItem(ACTIVITY_SYNC_KEY, now);

      // Trigger storage event for cross-tab sync (doesn't fire in same tab)
      // Other tabs will listen to this via storage event
    }

    // Set warning timer (1 minute before actual logout)
    const warningTime = (timeoutInMinutes - 1) * 60 * 1000;
    if (warningTime > 0) {
      warningTimeoutRef.current = setTimeout(() => {
        handleIdleWarning();
      }, warningTime);
    }

    // Set actual logout timer
    const logoutTime = timeoutInMinutes * 60 * 1000;
    timeoutRef.current = setTimeout(() => {
      // Mark idle logout in progress
      SessionExpiryTimer.setIdleLogoutInProgress(true);
      handleLogout();
    }, logoutTime);
  }, [timeoutInMinutes, handleIdleWarning, handleLogout, modalRef]);

  const setupActivityListeners = useCallback((onActivity: () => void) => {
    const events = [
      "mousedown",
      "mouseup",
      "keypress",
      "keydown",
      "keyup",
      "DOMMouseScroll",
      "mousewheel",
      "touchmove",
      "touchstart",
      "touchend",
      "scroll",
      "click",
      "focus",
      "blur",
      "input",
      "change",
      "select",
      "drag",
      "drop",
    ];

    // Throttle activity detection to avoid excessive timer resets
    let lastActivity = 0;
    const throttledActivity = () => {
      const now = Date.now();
      if (now - lastActivity > 1000) {
        // Only reset timer once per second
        lastActivity = now;
        onActivity();
      }
    };

    // Handle tab visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // User came back to tab - reset timer
        onActivity();
      }
    };

    // Handle cross-tab activity sync via storage events
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACTIVITY_SYNC_KEY && e.newValue) {
        // Activity detected in another tab - reset timer
        onActivity();
      }
      if (e.key === SESSION_LOGOUT_KEY && e.newValue) {
        // Logout triggered in another tab - logout here too
        handleLogout();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, throttledActivity, { passive: true });
    });

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, throttledActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [handleLogout]);

  useEffect(() => {
    if (!isAuthenticated || !timeoutInMinutes || timeoutInMinutes <= 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      return;
    }

    // Initial timer setup
    resetTimer();

    // Setup activity listeners
    const cleanup = setupActivityListeners(resetTimer);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (cleanup) {
        cleanup();
      }
    };
  }, [isAuthenticated, timeoutInMinutes, resetTimer, setupActivityListeners]);
}
